import React, { useState } from 'react';
import { Card, List, Badge, Tag, Button, Empty, Spin, Alert as AntAlert, Collapse } from 'antd';
import {
  WarningOutlined,
  InfoCircleOutlined,
  CloseCircleOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  ExperimentOutlined,
  TeamOutlined,
  ToolOutlined,
  BellOutlined,
} from '@ant-design/icons';
import { useDashboardAlerts, Alert } from '../../../hooks/useManufacturingDashboard';
import { useNavigate } from 'react-router-dom';

interface AlertsPanelProps {
  refreshInterval?: number;
}

const AlertsPanel: React.FC<AlertsPanelProps> = ({ refreshInterval }) => {
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['critical']);
  const { data: alerts, isLoading, error } = useDashboardAlerts(refreshInterval);
  const navigate = useNavigate();

  const getAlertIcon = (type: string) => {
    const icons: Record<string, React.ReactNode> = {
      'bottleneck': <ThunderboltOutlined />,
      'delay': <ClockCircleOutlined />,
      'quality': <ExperimentOutlined />,
      'resource': <TeamOutlined />,
      'maintenance': <ToolOutlined />,
    };
    return icons[type] || <InfoCircleOutlined />;
  };

  const getAlertColor = (severity: string) => {
    const colors: Record<string, string> = {
      'critical': '#ff4d4f',
      'warning': '#faad14',
      'info': '#145A54',
    };
    return colors[severity] || '#145A54';
  };

  const handleAlertClick = (alert: Alert) => {
    if (alert.workOrderId) {
      navigate(`/mfg/work-orders/${alert.workOrderId}`);
    } else if (alert.workstationId) {
      navigate('/manufacturing/workstations');
    }
  };

  const renderAlertList = (alertList: Alert[], severity: string) => {
    if (!alertList || alertList.length === 0) {
      return (
        <Empty
          description={`No ${severity} alerts`}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: '20px 0' }}
        />
      );
    }

    return (
      <List
        dataSource={alertList}
        renderItem={(alert) => (
          <List.Item
            style={{
              borderLeft: `4px solid ${getAlertColor(alert.severity)}`,
              paddingLeft: 12,
              cursor: alert.workOrderId || alert.workstationId ? 'pointer' : 'default',
            }}
            onClick={() => handleAlertClick(alert)}
          >
            <List.Item.Meta
              avatar={
                <div style={{ fontSize: 24, color: getAlertColor(alert.severity) }}>
                  {getAlertIcon(alert.type)}
                </div>
              }
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 500 }}>{alert.title}</span>
                  <Tag color={getAlertColor(alert.severity)}>{alert.severity}</Tag>
                  <Tag>{alert.type}</Tag>
                </div>
              }
              description={
                <div>
                  <div style={{ marginBottom: 4 }}>{alert.description}</div>
                  {alert.actionRequired && (
                    <div style={{ fontSize: 12, color: '#145A54', fontStyle: 'italic' }}>
                      → {alert.actionRequired}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                    {new Date(alert.timestamp).toLocaleString()}
                  </div>
                </div>
              }
            />
          </List.Item>
        )}
      />
    );
  };

  if (error) {
    return (
      <Card title="Alerts & Notifications">
        <AntAlert
          message="Error Loading Alerts"
          description={error.message}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card title="Alerts & Notifications">
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  const totalAlerts = (alerts?.critical?.length || 0) + 
                      (alerts?.warnings?.length || 0) + 
                      (alerts?.info?.length || 0);

  const collapseItems = [
    {
      key: 'critical',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
          <span style={{ fontWeight: 500 }}>Critical Alerts</span>
          <Badge count={alerts?.critical?.length || 0} style={{ backgroundColor: '#ff4d4f' }} />
        </div>
      ),
      children: renderAlertList(alerts?.critical || [], 'critical'),
    },
    {
      key: 'warnings',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <WarningOutlined style={{ color: '#faad14' }} />
          <span style={{ fontWeight: 500 }}>Warnings</span>
          <Badge count={alerts?.warnings?.length || 0} style={{ backgroundColor: '#faad14' }} />
        </div>
      ),
      children: renderAlertList(alerts?.warnings || [], 'warning'),
    },
    {
      key: 'info',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <InfoCircleOutlined style={{ color: '#145A54' }} />
          <span style={{ fontWeight: 500 }}>Information</span>
          <Badge count={alerts?.info?.length || 0} style={{ backgroundColor: '#145A54' }} />
        </div>
      ),
      children: renderAlertList(alerts?.info || [], 'info'),
    },
  ];

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BellOutlined />
          <span>Alerts & Notifications</span>
          <Badge count={totalAlerts} showZero />
        </div>
      }
      extra={
        <Button type="link" size="small">
          View All
        </Button>
      }
    >
      {totalAlerts === 0 ? (
        <Empty
          description="No alerts at the moment"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: '40px 0' }}
        />
      ) : (
        <Collapse
          activeKey={expandedCategories}
          onChange={(keys) => setExpandedCategories(keys as string[])}
          items={collapseItems}
          defaultActiveKey={['critical']}
        />
      )}
    </Card>
  );
};

export default AlertsPanel;
